import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, DatePicker, Space, Typography,
  message, Spin, Row, Col, Button
} from 'antd';
import { 
  CalendarOutlined, ClockCircleOutlined 
} from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import api, { hourlyDataService } from '../../services/api';
import HourlyDataChart from '../HourlyReport/components/HourlyDataChart';
import HourlyHeatmapTable from '../HourlyReport/components/HourlyHeatmapTable';
import HourlyMetricsComparisonTable from '../HourlyReport/components/HourlyMetricsComparisonTable';
import SummaryCards from '../HourlyReport/components/SummaryCards';
import 'dayjs/locale/tr';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('tr');

const SingleBrandHourlyReport = ({ brandId, brandName }) => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('day'),
    dayjs().endOf('day')
  ]);
  const [hourlyData, setHourlyData] = useState([]);
  const [previousHourlyData, setPreviousHourlyData] = useState([]);

  // Saatlik verileri yükle
  const loadHourlyData = useCallback(async () => {
    if (!brandId || !dateRange[0] || !dateRange[1]) return;

    setLoading(true);
    try {
      // Türkiye saatinde gün başı/sonu yap, sonra UTC'ye çevir
      const startDate = dayjs.tz(dateRange[0], 'Europe/Istanbul').startOf('day').utc().toISOString();
      const endDate = dayjs.tz(dateRange[1], 'Europe/Istanbul').endOf('day').utc().toISOString();

      // Seçili dönem verilerini çek
      const data = await hourlyDataService.getHourlyData(
        brandId,
        startDate,
        endDate
      );

      // Karşılaştırma için önceki dönem verilerini hesapla
      const daysDiff = dayjs(dateRange[1]).diff(dayjs(dateRange[0]), 'day') + 1;
      const previousStartDate = dayjs.tz(dateRange[0], 'Europe/Istanbul')
        .subtract(daysDiff, 'day')
        .startOf('day')
        .utc()
        .toISOString();
      const previousEndDate = dayjs.tz(dateRange[1], 'Europe/Istanbul')
        .subtract(daysDiff, 'day')
        .endOf('day')
        .utc()
        .toISOString();

      // Önceki dönem verilerini çek
      const previousData = await hourlyDataService.getHourlyData(
        brandId,
        previousStartDate,
        previousEndDate
      );

      setHourlyData(data);
      setPreviousHourlyData(previousData);
    } catch (error) {
      console.error('Saatlik veriler yüklenirken hata:', error);
      message.error('Saatlik veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [brandId, dateRange]);

  useEffect(() => {
    loadHourlyData();
  }, [loadHourlyData]);

  // Hızlı tarih filtreleri
  const setQuickDateRange = (type) => {
    const today = dayjs();
    
    switch (type) {
      case 'today':
        setDateRange([today.startOf('day'), today.endOf('day')]);
        break;
      case 'yesterday':
        const yesterday = today.subtract(1, 'day');
        setDateRange([yesterday.startOf('day'), yesterday.endOf('day')]);
        break;
      case 'last7days':
        setDateRange([today.subtract(6, 'day').startOf('day'), today.endOf('day')]);
        break;
      case 'thisweek':
        setDateRange([today.startOf('week'), today.endOf('day')]);
        break;
      default:
        break;
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <ClockCircleOutlined />
            {brandName} - Saatlik Rapor
          </Title>
          <Text type="secondary">
            {brandName} markasının saatlik performans verileri
          </Text>
        </div>

        {/* Kontrol Paneli */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <div>
              <Text strong>Tarih Aralığı</Text>
              <RangePicker
                style={{ width: '100%', marginTop: 4 }}
                value={dateRange}
                onChange={setDateRange}
                format="DD.MM.YYYY"
                placeholder={['Başlangıç', 'Bitiş']}
              />
            </div>
          </Col>

          <Col xs={24} lg={12}>
            {/* Boş alan */}
          </Col>
        </Row>

        {/* Hızlı Tarih Filtreleri */}
        <Row style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Text strong style={{ marginRight: 12 }}>Hızlı Filtreler:</Text>
            <Space wrap>
              <Button 
                size="small" 
                icon={<CalendarOutlined />}
                onClick={() => setQuickDateRange('today')}
              >
                Bugün
              </Button>
              <Button 
                size="small"
                onClick={() => setQuickDateRange('yesterday')}
              >
                Dün
              </Button>
              <Button 
                size="small"
                onClick={() => setQuickDateRange('last7days')}
              >
                Son 7 Gün
              </Button>
              <Button 
                size="small"
                onClick={() => setQuickDateRange('thisweek')}
              >
                Bu Hafta
              </Button>
            </Space>
          </Col>
        </Row>

        <Spin spinning={loading}>
          {/* Heatmap Tablosu */}
          <div style={{ marginBottom: 32 }}>
            <HourlyHeatmapTable 
              data={hourlyData}
              loading={false}
            />
          </div>

          {/* Saatlik Metrik Karşılaştırma Tablosu */}
          <div style={{ marginBottom: 32 }}>
            <HourlyMetricsComparisonTable 
              data={hourlyData}
              previousData={previousHourlyData}
              loading={loading}
            />
          </div>

          {/* Özet Kartlar */}
          <SummaryCards data={hourlyData} previousData={previousHourlyData} />

          {/* Saatlik Performans Grafiği */}
          <div style={{ marginBottom: 32 }}>
            <Card>
              <div style={{ marginBottom: 16 }}>
                <Title level={4} style={{ margin: 0 }}>
                  Saatlik Performans Grafiği
                </Title>
                <Text type="secondary">
                  Gelir ve maliyetlerin saatlik dağılımı
                </Text>
              </div>
              <HourlyDataChart 
                data={hourlyData}
                loading={loading}
              />
            </Card>
          </div>

        </Spin>

      </Card>
    </div>
  );
};

export default SingleBrandHourlyReport;